/* eslint-disable no-undef */
'use strict';

(function (factory, window) {
    // define an AMD module that relies on 'leaflet'
    if (typeof define === 'function' && define.amd) {
        define(['leaflet'], factory);

        // define a Common JS module that relies on 'leaflet'
    } else if (typeof exports === 'object') {
        module.exports = factory(require('leaflet'));
    }

    // attach your plugin to the global 'L' variable
    if (typeof window !== 'undefined' && window.L) {
        window.L.CanvasIconLayer = factory(L);
    }
})(function (L) {
    const CanvasIconLayer = (L.Layer ? L.Layer : L.Class).extend({
        initialize: function (options) {
            L.setOptions(this, options);
            this._markers = {};
        },
        setOptions: function (options) {
            L.setOptions(this, options);

            if (this._canvas) {
                this._updateOptions();
            }

            return this.redraw();
        },
        redraw: function () {
            this._redraw(true);
        },
        addMarker: function (marker) {
            L.Util.stamp(marker);

            this._markers[marker._leaflet_id] = marker;
            this._drawMarker(marker);
        },
        resetMarkers: function (markers) {
            if (!markers) {
                markers = [];
            }

            const srcMarkers = Object.values(this._markers);
            const newMarkers = {};
            let isNonePopupOpened = true;

            markers.forEach((marker) => {
                const filterRes = srcMarkers.filter((m) => m.options.properties.vehicleNo === marker.options.properties.vehicleNo);
                if (filterRes.length) {
                    filterRes[0].setLatLng(marker.getLatLng());

                    if (filterRes[0]._popup && filterRes[0]._popup.isOpen()) {
                        filterRes[0]._popup.setLatLng(marker.getLatLng());
                        isNonePopupOpened = false;
                    }

                    newMarkers[filterRes[0]._leaflet_id] = filterRes[0];
                } else {
                    L.Util.stamp(marker);
                    newMarkers[marker._leaflet_id] = marker;
                }
            });

            if (isNonePopupOpened && this._map._popup) {
                this._map.closePopup(this._map._popup);
            }

            this._markers = { ...newMarkers };
            this._reset();
        },
        addLayer: function (layer) {
            if (layer.options.pane === 'markerPane' && layer.options.icon) {
                this.addMarker(layer);
            }
        },
        removeLayer: function (layer) {
            this.removeMarker(layer, true);
        },
        // eslint-disable-next-line no-unused-vars
        removeMarker: function (marker, redraw) {
            if (marker._tooltip) {
                marker._tooltip.close();
            }

            delete this._markers[marker._leaflet_id];
        },
        onAdd: function (map) {
            this._map = map;
            this._onClickListeners = [];
            this._onMouseMoveListeners = [];

            if (!this._canvas) {
                this._initCanvas();
            }

            if (this.options.pane) {
                this.getPane().appendChild(this._canvas);
            } else {
                map._panes.overlayPane.appendChild(this._canvas);
            }

            map.on('moveend', this._reset, this);
            map.on('zoomstart', this._clearLayer, this);
            map.on('mousemove', this._handleMouseHover, this);
            map.on('click', this._executeClickListeners, this);

            this._reset();
        },
        _clearLayer: function () {
            this._context && this._context.clearRect(0, 0, this._canvas.width, this._canvas.height);

            Object.keys(this._markers).forEach(function (item) {
                this._markers[item]._tooltip && this._markers[item]._tooltip.setOpacity(0);
            }, this);
        },
        _handleMouseHover: function (event) {
            L.DomUtil.removeClass(this._canvas, 'leaflet-canvas-interactive');
            for (let markerId in this._markers) {
                const marker = this._markers[markerId];
                const point = this._map.latLngToContainerPoint(this._markers[markerId].getLatLng());

                if (this._hit(marker, point, event)) {
                    L.DomUtil.addClass(this._canvas, 'leaflet-canvas-interactive');
                    break;
                }
            }
        },
        onRemove: function (map) {
            if (this.options.pane) {
                this.getPane().removeChild(this._canvas);
            } else {
                map.getPanes().overlayPane.removeChild(this._canvas);
            }

            map.off('moveend', this._reset, this);
            map.off('zoomstart', this._clearLayer, this);
            map.off('mousemove', this._handleMouseHover, this);
            map.off('click', this._executeClickListeners, this);

            Object.keys(this._markers).forEach(function (item) {
                const popup = this._markers[item]._popup;

                if (popup && popup.isOpen()) {
                    map.closePopup(popup);
                    return false;
                }
            }, this);
        },
        addTo: function (map) {
            map.addLayer(this);
            return this;
        },
        _drawMarker: function (marker) {
            if (!this._map) {
                return;
            }

            const self = this;
            const zoom = self._map.getZoom();
            const icon = marker.options.icon.options.html || marker.options.icon.options.iconUrl;
            const isHtml = !!marker.options.icon.options.html;
            const pointPos = self._map.latLngToContainerPoint(marker.getLatLng());

            if (!marker.canvas_img) {
                marker.canvas_img = new Image();
            }

            marker.canvas_img.src = isHtml ? 'data:image/svg+xml;base64,' + btoa(icon) : icon;
            self._drawImage(marker, pointPos);

            if (marker._tooltip) {
                self._map.getBounds().contains(marker.getLatLng()) && zoom >= marker.options.minTooltipZoom ? marker._tooltip.setLatLng(marker._latlng).openOn(self._map).setOpacity(1) : marker._tooltip.close();
            }
        },
        _drawImage: function (marker, pointPos) {
            const x = pointPos.x - marker.options.icon.options.iconAnchor[0];
            const y = pointPos.y - marker.options.icon.options.iconAnchor[1];
            const w = marker.options.icon.options.iconSize[0];
            const h = marker.options.icon.options.iconSize[1];

            Object.prototype.hasOwnProperty.call(marker.options, 'rotationAngle') ? this._rotate(marker, x, y, w, h) : this._context.drawImage(marker.canvas_img, x, y, w, h);
        },
        _rotate: function (marker, x, y, w, h) {
            this._context.save();
            this._context.translate(x + w / 2, y + h / 2);
            this._context.rotate((marker.options.rotationAngle * Math.PI) / 180);
            this._context.translate(-x - w / 2, -y - h / 2);
            this._context.drawImage(marker.canvas_img, x, y, w, h);
            this._context.restore();
        },
        _reset: function () {
            const topLeft = this._map.containerPointToLayerPoint([0, 0]);
            L.DomUtil.setPosition(this._canvas, topLeft);

            const size = this._map.getSize();

            this._canvas.width = size.x;
            this._canvas.height = size.y;
            this._redraw();
        },
        _redraw: function (clear) {
            if (!this._map) {
                return;
            }

            if (clear) {
                this._context.clearRect(0, 0, this._canvas.width, this._canvas.height);
            }

            Object.keys(this._markers).forEach(function (item) {
                this._drawMarker(this._markers[item]);
            }, this);
        },
        _initCanvas: function () {
            this._canvas = L.DomUtil.create('canvas', 'leaflet-canvas-icon-layer leaflet-layer');
            const originProp = L.DomUtil.testProp(['transformOrigin', 'WebkitTransformOrigin', 'msTransformOrigin']);
            this._canvas.style[originProp] = '50% 50%';

            const size = this._map.getSize();
            this._canvas.width = size.x;
            this._canvas.height = size.y;
            this._context = this._canvas.getContext('2d');

            L.DomUtil.addClass(this._canvas, 'leaflet-zoom-' + (this._map.options.zoomAnimation && L.Browser.any3d ? 'animated' : 'hide'));
        },
        _updateOptions: function () {},
        addOnClickListener: function (listener) {
            if (!this._onClickListeners) {
                this._onClickListeners = [];
            }

            this._onClickListeners.push(listener);
        },
        _executeClickListeners: function (event) {
            for (let markerId in this._markers) {
                const marker = this._markers[markerId];
                const point = this._map.latLngToContainerPoint(this._markers[markerId].getLatLng());

                if (this._hit(marker, point, event)) {
                    this._onClickListeners.forEach(function (listener) {
                        listener(event, marker);
                    });
                    break;
                }
            }
        },
        _hit: function (marker, point, event) {
            const width = marker.options.icon.options.iconSize[0];
            const height = marker.options.icon.options.iconSize[1];

            const top = marker.options.icon.options.iconAnchor[1] + 1;
            const bottom = height - marker.options.icon.options.iconAnchor[1] + 1;
            const left = marker.options.icon.options.iconAnchor[0] + 1;
            const right = width - marker.options.icon.options.iconAnchor[0] + 1;

            const x = event.containerPoint.x;
            const y = event.containerPoint.y;

            return x <= point.x + right && x >= point.x - left && y >= point.y - top && y <= point.y + bottom;
        },
    });

    L.canvasIconLayer = function (options) {
        return new CanvasIconLayer(options);
    };
}, window);
